import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { DaysOfWeekSelect } from './DaysOfWeekSelect'
import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { useEffect, useState } from 'react'
import {
  buildCronExpression,
  CronBuilderState,
  DAY_OF_MONTH_OPTIONS,
  DEFAULT_CRON_BUILDER_STATE,
  FREQUENCY_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_INTERVAL_OPTIONS,
  MINUTE_OPTIONS,
  parseCronExpression,
} from './cronExpressionUtils'
import { Badge } from '@/components/shadcn/badge'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconV2IncorrectOutline } from '@icons/IconV2IncorrectOutline'

type CronExpressionFieldProps = {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  className?: string
  portalPopover?: boolean
}

const compactTriggerClassName = 'h-10 w-auto min-w-[5.5rem] shrink-0 px-3'

export function CronExpressionField({
  value,
  onChange,
  onBlur,
  className,
  portalPopover = true,
}: CronExpressionFieldProps) {
  const [state, setState] = useState<CronBuilderState>(() => parseCronExpression(value) ?? DEFAULT_CRON_BUILDER_STATE)
  const [isCustomExpression, setIsCustomExpression] = useState(() => !parseCronExpression(value) && !!value.trim())

  useEffect(() => {
    const parsed = parseCronExpression(value)
    if (parsed) {
      setIsCustomExpression(false)
      setState(parsed)
      return
    }

    if (value.trim()) {
      setIsCustomExpression(true)
    }
  }, [value])

  useEffect(() => {
    if (isCustomExpression) return

    const nextExpression = buildCronExpression(state)
    if (nextExpression !== value) {
      onChange(nextExpression)
    }
  }, [isCustomExpression, onChange, state, value])

  const updateState = (patch: Partial<CronBuilderState>) => {
    setIsCustomExpression(false)
    setState((current) => ({ ...current, ...patch }))
    onBlur?.()
  }

  const handleClear = () => {
    setIsCustomExpression(false)
    setState(DEFAULT_CRON_BUILDER_STATE)
    onChange(buildCronExpression(DEFAULT_CRON_BUILDER_STATE))
    onBlur?.()
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Every</span>
        <SingleSelectCombobox
          options={FREQUENCY_OPTIONS}
          selectedValue={state.frequency}
          onSelectionChange={(frequency) => {
            if (!frequency) return
            updateState({ frequency })
          }}
          allowClear={false}
          className={compactTriggerClassName}
          portalPopover={portalPopover}
        />

        {state.frequency === 'week' && (
          <>
            <span className="text-sm text-muted-foreground">on</span>
            <DaysOfWeekSelect
              selectedDays={state.daysOfWeek}
              onSelectionChange={(daysOfWeek) => updateState({ daysOfWeek })}
            />
          </>
        )}

        {state.frequency === 'month' && (
          <>
            <span className="text-sm text-muted-foreground">on day</span>
            <SingleSelectCombobox
              options={DAY_OF_MONTH_OPTIONS}
              selectedValue={state.dayOfMonth}
              onSelectionChange={(dayOfMonth) => {
                if (dayOfMonth == null) return
                updateState({ dayOfMonth })
              }}
              allowClear={false}
              className={compactTriggerClassName}
              portalPopover={portalPopover}
            />
          </>
        )}

        {state.frequency === 'minute' && (
          <>
            <SingleSelectCombobox
              options={MINUTE_INTERVAL_OPTIONS}
              selectedValue={state.minuteInterval}
              onSelectionChange={(minuteInterval) => {
                if (minuteInterval == null) return
                updateState({ minuteInterval })
              }}
              allowClear={false}
              className={compactTriggerClassName}
              portalPopover={portalPopover}
            />
            <span className="text-sm text-muted-foreground">minute(s)</span>
          </>
        )}

        {(state.frequency === 'day' || state.frequency === 'week' || state.frequency === 'month') && (
          <>
            <span className="text-sm text-muted-foreground">at</span>
            <SingleSelectCombobox
              options={HOUR_OPTIONS}
              selectedValue={state.hour}
              onSelectionChange={(hour) => {
                if (hour == null) return
                updateState({ hour })
              }}
              allowClear={false}
              className={cn(compactTriggerClassName, 'min-w-24')}
              portalPopover={portalPopover}
            />
            <span className="text-sm text-muted-foreground">:</span>
            <SingleSelectCombobox
              options={MINUTE_OPTIONS}
              selectedValue={state.minute}
              onSelectionChange={(minute) => {
                if (minute == null) return
                updateState({ minute })
              }}
              allowClear={false}
              className={cn(compactTriggerClassName, 'min-w-24')}
              portalPopover={portalPopover}
            />
          </>
        )}

        {state.frequency === 'hour' && (
          <>
            <span className="text-sm text-muted-foreground">at minute</span>
            <SingleSelectCombobox
              options={MINUTE_OPTIONS}
              selectedValue={state.minute}
              onSelectionChange={(minute) => {
                if (minute == null) return
                updateState({ minute })
              }}
              allowClear={false}
              className={cn(compactTriggerClassName, 'min-w-24')}
              portalPopover={portalPopover}
            />
          </>
        )}

        <TooltipButton tooltip="Clear">
          <Button onClick={handleClear} type="button" variant="ghost" size="icon" className="size-10 shrink-0">
            <IconV2IncorrectOutline />
          </Button>
        </TooltipButton>
        <div className="ml-auto mr-0 flex h-10 shrink-0 items-center rounded-md bg-secondary px-2 font-mono text-xs">
          {isCustomExpression ? value : buildCronExpression(state)}
          {/* <span className="text-xs text-muted-foreground">Expression:</span> */}
          {/* <Badge className="font-mono" variant="secondary">
            {isCustomExpression ? value : buildCronExpression(state)}
          </Badge> */}
        </div>
      </div>

      {isCustomExpression && (
        <p className="text-xs text-muted-foreground">
          Using custom expression: <span className="font-mono">{value}</span>. Adjust the schedule above to replace it.
        </p>
      )}

      {/* <div>
        <span className="text-xs text-muted-foreground">Cron expression:</span>
        <Badge className="ml-2 bg-background font-mono" variant="outline">
          {isCustomExpression ? value : buildCronExpression(state)}
        </Badge>
      </div> */}
    </div>
  )
}
